import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { currentPeriod } from '@/components/dashboard/period-picker';
import { findDashboard } from '@/components/dashboard/registry';
import { PageHeader } from '@/components/layout/page-header';
import { PageShell } from '@/components/layout/page-shell';

const search = z.object({
  /**
   * Reporting month, `YYYY-MM`. Absent means "the current month".
   *
   * `.catch(undefined)` so a hand-edited or stale URL degrades to the default
   * month instead of throwing out of search validation and dropping the whole
   * route onto the error boundary for a cosmetic parameter.
   */
  period: z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/)
    .optional()
    .catch(undefined),
});

export const Route = createFileRoute('/_authenticated/dashboard/$dashboardId')({
  validateSearch: search,
  component: DashboardPage,
});

function DashboardPage() {
  const { dashboardId } = Route.useParams();
  const { period } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const dashboard = findDashboard(dashboardId);

  if (!dashboard) {
    return (
      <PageShell>
        <PageHeader title="Dashboard not found" subtitle={`No dashboard named "${dashboardId}".`} />
        <Link
          to="/dashboard"
          className="text-sm font-medium text-primary underline-offset-4 hover:underline"
        >
          Go to the first dashboard
        </Link>
      </PageShell>
    );
  }

  const View = dashboard.component;
  return (
    <View
      dashboard={dashboard}
      period={period ?? currentPeriod()}
      // `replace` so paging through months doesn't stack history entries the
      // user then has to click Back through.
      onPeriodChange={(next) => navigate({ search: { period: next }, replace: true })}
    />
  );
}
